import { Formatters } from '../shared/lib/formatters';
import { LoadingSpinner } from '../shared/components';
import { emptyState } from '../shared/components/Narrative';
import { icon } from '../shared/components/icons';
import { bindKebabs, closeKebab } from '../shared/components/Kebab';
import {
  OUTCOME_LABEL, STATE_LABEL, STATE_PILL,
  stateOf, isClosed, filterRows, stateCounts, recoveryKpis, bumpAggregates,
  groupForOwners, ownerCallText, parseTs, forbiddenKind,
} from '../../../lib/guestRecovery.mjs';
import type { RecoveryOutcome, RecoveryFilter, RecoveryCounts, RecoveryKpis, OwnerGroup } from '../../../lib/guestRecovery.mjs';
// R115 guest: the composer + timeline are shared with the Calendar's Recovery action
import { recoveryComposerHtml, recoveryTimelineHtml, composerGuard, RECOVERY_COMPOSER_CSS } from '../shared/components/RecoveryComposer';

// Guest Recovery — reservations/conversations whose guest messages read
// NEGATIVE, surfaced so the team can smooth things over before the review
// lands. Detection runs hourly server-side; this page is the worklist:
// open → reach out → log what you did → close it with an OUTCOME.
// Backed by GET/POST /api/assetops/issues/guest-recovery* (office only).
//
// R111: a first-class Workspace page (it used to be a tab of the Tasks &
// Issues hub). Drawn on main.css tokens + shared classes — .dash-head,
// .kpi-card band, .seg-tabs worklist filter, .pill severity/status, .btn
// actions, .input note — no bare hex, no var(--x, #hex) fallbacks. The head
// sits in .page-intro so a hub (which prints its own intro) still nests it.
//
// R114 #6 (Jackie: "the owner said no to fans / a portable AC and the only
// button was Mark smoothed"): four outcomes — Smoothed · Partly resolved ·
// Couldn't resolve · Blocked by owner — each a tab with a TRUE count (the
// server sends counts/kpis over everything, not the capped list); every step
// is an append-only Activity entry ("＋ Log action" never closes, works on
// closed cases too); every outcome but Smoothed needs a note (checked here
// and on the server). The Blocked-by-owner tab groups per owner (else per
// property) with "Copy for owner call" — no guest names / phones / emails.
// The composer keeps an unsent draft per viewer in localStorage (it replaced
// the R77 server autosave: typed text survives reloads and is never lost, and
// only a logged step reaches the team). All rules: lib/guestRecovery.mjs.
const API = '/api/assetops';
const SEV_PILL: Record<string, string> = { emergency: 'pill-red', high: 'pill-red', med: 'pill-amber', low: 'pill-gray' };
type Filter = RecoveryFilter;
const TAB_LABEL: Record<Filter, string> = { open: 'Open', smoothed: OUTCOME_LABEL.smoothed, partial: OUTCOME_LABEL.partial, unresolved: OUTCOME_LABEL.unresolved, blocked_owner: OUTCOME_LABEL.blocked_owner, all: 'All' };
const TABS: Filter[] = ['open', 'smoothed', 'partial', 'unresolved', 'blocked_owner', 'all'];
const EMPTY_COPY: Record<Filter, string> = {
  open: 'No open cases — nothing reads negative right now; the hourly scan keeps watching.',
  smoothed: 'Nothing marked smoothed yet.',
  partial: 'No cases closed as partly resolved.',
  unresolved: "No cases closed as couldn't resolve.",
  blocked_owner: 'No guest requests blocked by an owner.',
  all: 'No cases.',
};
const DRAFT_KEY = 'gr_draft:';

export class GuestRecoveryPage {
  private container: HTMLElement;
  private tickets: any[] | null = null;         // null = load failed
  private forbidden = false;                    // R114 fix: the GET answered 403 (not an office role)
  private unverified = false;                   // R114 fix2: 403 "role could not be verified" (a blip) — retry, not the lock
  private counts: RecoveryCounts | null = null; // R114: true tab counts (server, uncapped)
  private kpis: RecoveryKpis | null = null;     // R114: true KPI band (server, uncapped)
  private loaded = new Set<Filter>();           // closed tabs fully fetched (?state=)
  private loadingTab: Filter | '' = '';
  private msgSync: any = null;                  // R108: the classifier feeding this list
  private open = new Set<string>();             // expanded ticket ids
  private drafts: Record<string, string> = {};  // composer drafts (mirrored per viewer in localStorage)
  private errs: Record<string, string> = {};    // inline per-row error ("note required")
  private note = '';                            // action feedback banner
  private noteBad = false;
  private busy = false;                         // gates double-submits
  private fStatus: Filter = 'open';             // R111: worklist filter — state on the instance, never in the DOM
  private groups: OwnerGroup<any>[] = [];       // R114: Blocked-by-owner tab groups (for the copy button)

  constructor(container: HTMLElement) { this.container = container; }

  // R114 fix (review recovery#3): a 403 (the list is office-only — an
  // inspector / maintenance user who followed a stale link) is NOT a server
  // failure: it comes back as { forbidden: true } and draws a friendly lock,
  // never "check the server log" with a Retry that can only 403 again.
  // R114 fix2 (rec2): ONLY the role gate's 403 is the lock. server.js also
  // answers 403 "role could not be verified" when a cold role cache meets a
  // Supabase blip — an office user then gets { unverified: true }: a
  // try-again message with Retry, never "limited to office roles".
  private async fetchList(params: Record<string, string> = {}): Promise<any | null> {
    const qs = new URLSearchParams(params).toString();
    return fetch(`${API}/issues/guest-recovery${qs ? `?${qs}` : ''}`).then(async r => {
      if (r.status === 403) return forbiddenKind(await r.json().catch(() => null)) === 'role' ? { forbidden: true } : { unverified: true };
      return r.ok ? r.json() : null;
    }).catch(() => null);
  }

  async render(): Promise<void> {
    this.container.innerHTML = `<div class="page-content"><div id="gr-body"><div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div></div></div>`;
    // R111: the Issues board's "✓ Guest Recovery" chip names a case — show it even if it is closed.
    // R114: the server always includes the focus row (by id, or the ticket a case took over).
    let focus = '';
    try { focus = sessionStorage.getItem('gr_focus') || ''; sessionStorage.removeItem('gr_focus'); } catch { /* private mode */ }
    const j0 = await this.fetchList(focus ? { focus } : {});
    this.forbidden = !!(j0 && j0.forbidden);
    this.unverified = !!(j0 && j0.unverified);
    const j = this.forbidden || this.unverified ? null : j0;
    this.tickets = j ? (j.tickets || []) : null;
    this.counts = j && j.counts ? j.counts : null;
    this.kpis = j && j.kpis ? j.kpis : null;
    this.msgSync = j ? (j.msg_sync || null) : null;
    this.loaded = new Set();
    // persistent instance: forget expanded ids that left the payload
    // (otherwise the refresh veto below never releases) and the one-shot banner
    if (this.tickets) {   // only against a real payload — a failed GET must not drop state kept for retry
      const live = new Set(this.tickets.map((t: any) => String(t.id)));
      for (const id of [...this.open]) if (!live.has(id)) this.open.delete(id);
      for (const id of Object.keys(this.errs)) if (!live.has(id)) delete this.errs[id];
      this.note = '';
    }
    let hitId = '';
    if (focus) {
      const hit = (this.tickets || []).find((t: any) => String(t.id) === focus || String(t.ticket_id || '') === focus);
      if (hit) { this.fStatus = stateOf(hit); hitId = String(hit.id); this.open.add(hitId); }
      else this.fStatus = 'all';
    }
    this.draw();
    if (hitId) this.container.querySelector(`.gr-row[data-id="${CSS.escape(hitId)}"]`)?.scrollIntoView({ block: 'center' });
    void this.ensureTab(this.fStatus);
  }

  // R111: this is now a persistent standalone instance that App.autoRefresh
  // re-renders every 15 min — veto while a step is saving, a closed tab is
  // loading, or a row is expanded (a redraw would collapse it). Unsent
  // drafts don't need the veto: they live in localStorage.
  suspendAutoRefresh(): boolean {
    if (this.busy || this.loadingTab) return true;
    // only an expanded row that is actually on screen counts (a filtered-out or vanished id must not park the timer)
    return [...this.open].some(id => !!this.container.querySelector(`.gr-row.expanded[data-id="${CSS.escape(id)}"]`));
  }

  private esc(s: any): string { return Formatters.escapeHtml(s == null ? '' : String(s)); }
  private profile(): any {
    try { return JSON.parse(sessionStorage.getItem('userProfile') || '{}') || {}; } catch (_) { return {}; }
  }
  // email = the durable actor key (same rationale as EodPage.actorEmail)
  private who(): string { return String(this.profile().email || '').trim().toLowerCase(); }
  private whoName(): string { return String(this.profile().full_name || '').trim(); }
  private todayPT(): string { return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' }); }

  // ── per-viewer drafts (localStorage can throw / be empty — never required) ──
  private draftKey(id: string): string { return `${DRAFT_KEY}${this.who() || 'anon'}:${id}`; }
  private draftOf(id: string): string {
    if (this.drafts[id] !== undefined) return this.drafts[id];
    try { return localStorage.getItem(this.draftKey(id)) || ''; } catch { return ''; }
  }
  private setDraft(id: string, v: string): void {
    if (v) this.drafts[id] = v; else delete this.drafts[id];
    try { if (v.trim()) localStorage.setItem(this.draftKey(id), v); else localStorage.removeItem(this.draftKey(id)); } catch { /* private mode / blocked storage: the in-memory draft still holds */ }
  }

  // message timestamps render in PT — the portfolio's timezone, not the viewer's.
  private fmtT(s: any): string {
    const ms = parseTs(s);
    if (!ms) return '—';
    const d = new Date(ms);
    return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/Los_Angeles' })} · ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Los_Angeles' })}`;
  }
  private fmtD(s: any): string {
    const d = new Date(`${String(s || '').slice(0, 10)}T00:00:00Z`);
    return isNaN(d.getTime()) ? String(s || '?') : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  }

  // KPI band. R114: the server's numbers are computed over EVERY row (the
  // list itself is capped); a missing kpis block (older server) falls back to
  // the loaded rows. "Smoothed this week" = outcome Smoothed only — a
  // blocked or unhappy close is not a win; those show in "Not smoothed".
  private kpisHtml(all: any[]): string {
    const k = this.kpis || recoveryKpis(all, Date.now(), this.todayPT());
    const card = (label: string, ico: string, v: number, cap: string, cls = '') => `
      <div class="kpi-card ${cls}">
        <div class="kpi-card-top"><span class="kpi-card-label">${label}</span><span class="kpi-card-ic">${icon(ico, 17)}</span></div>
        <div class="kpi-card-value">${v}</div>
        <div class="kpi-card-cap">${cap}</div>
      </div>`;
    const b = k.byOutcome30 || { partial: 0, unresolved: 0, blocked_owner: 0 };
    return `
      <div class="kpi-grid gr-kpis">
        ${card('Open cases', 'heart', k.open, k.inHouse ? `${k.inHouse} staying tonight` : k.open ? 'waiting on outreach' : 'nothing open right now', k.open ? 'tone-bad' : 'tone-good')}
        ${card('New this week', 'chat', k.newWeek, 'cases with a message in the last 7 days')}
        ${card(k.stamped ? 'Smoothed this week' : 'Smoothed', 'sparkles', k.smoothedWeek, k.stamped ? 'closed as Smoothed in the last 7 days' : 'all-time, marked by the team')}
        ${card('Not smoothed · 30 days', 'flag', k.notSmoothed30, `${b.partial} partly · ${b.unresolved} couldn't resolve · ${b.blocked_owner} blocked by owner`, k.notSmoothed30 ? 'tone-warn' : '')}
      </div>`;
  }

  // ── Activity timeline: the recovery log + the case's system audit ──────
  // R115 guest: shared with the Calendar (shared/components/RecoveryComposer)
  private timelineHtml(t: any): string {
    return recoveryTimelineHtml(t, { esc: (x) => this.esc(x), fmtT: (x) => this.fmtT(x) });
  }

  private rowHtml(t: any): string {
    const id = String(t.id);
    const isOpen = this.open.has(id);
    const state = stateOf(t);
    const closed = isClosed(t);
    const draft = this.draftOf(id);
    const sev = String(t.severity || 'med');
    return `
      <div class="gr-row ${isOpen ? 'expanded' : ''}" data-id="${this.esc(id)}">
        <div class="gr-line" data-tog="1">
          <span class="pill ${SEV_PILL[sev] || 'pill-amber'}">${this.esc(sev.toUpperCase())}</span>
          <div class="gr-main">
            <div class="gr-guest">
              <span class="gr-name">${this.esc(t.guest_name || 'Guest')}</span>
              ${t.origin === 'issues-board' ? `<span class="pill pill-blue" title="Sent here from the Issues board${(t.items || []).length ? `: ${this.esc((t.items || []).map((x: any) => x.title).join('; '))}` : ''}">📋 Issues board</span>` : ''}
              ${t.origin === 'calendar' ? `<span class="pill pill-blue" title="Started from a reservation on the Calendar">🗓 Calendar</span>` : ''}
              ${t.check_in ? `<span class="gr-dates" title="Stay dates">${this.esc(this.fmtD(t.check_in))} → ${this.esc(this.fmtD(t.check_out))}</span>` : ''}
            </div>
            <div class="gr-sum">${this.esc(t.summary || '')}</div>
          </div>
          <div class="gr-right">
            <span class="gr-listing">${this.esc(t.listing_name || '—')}</span>
            <span class="gr-when">${this.fmtT(t.last_message_at)}</span>
            <span class="pill ${STATE_PILL[state]}">${this.esc(STATE_LABEL[state])}</span>
            <span class="gr-caret">${isOpen ? '▾' : '▸'}</span>
          </div>
        </div>
        ${isOpen ? `
        <div class="gr-detail">
          ${t.excerpt ? `<div class="gr-excerpt">${this.esc(t.excerpt)}</div>` : ''}
          <div class="gr-sec">Activity</div>
          ${this.timelineHtml(t)}
          ${recoveryComposerHtml({ closed, state, draft, err: this.errs[id], busy: this.busy, smoothedAt: t.smoothed_at || null, draftHint: 'Unsent draft — saved on this device only. Log it or pick an outcome to share it with the team.', esc: (x) => this.esc(x), fmtT: (x) => this.fmtT(x) })}
        </div>` : ''}
      </div>`;
  }

  private groupsHtml(rows: any[]): string {
    this.groups = groupForOwners(rows);
    return this.groups.map(g => `
      <div class="gr-group">
        <div class="gr-group-h">
          <div class="gr-group-tt">
            <div class="gr-group-t">${this.esc(g.label)} <span class="gr-n">${g.rows.length}</span></div>
            <div class="gr-group-s">${g.kind === 'owner' ? `Owner · ${this.esc(g.listings.join(', '))}` : 'Property · no owner assigned yet'}</div>
          </div>
          <button type="button" class="btn btn-secondary btn-sm gr-btn" data-gr-copy="${this.esc(g.key)}" title="Copies date · property · what the guest asked · what we did / the owner's answer — no guest names or phone numbers">⧉ Copy for owner call</button>
        </div>
        ${g.rows.map(r => this.rowHtml(r)).join('')}
      </div>`).join('');
  }

  private draw(): void {
    const body = document.getElementById('gr-body');
    if (!body) return;
    closeKebab();
    // a redraw must never eat typing: keep the focused composer + caret
    const ae = document.activeElement as HTMLTextAreaElement | null;
    const keep = ae && ae.dataset && ae.dataset.role === 'gr-note' ? { id: (ae.closest('.gr-row') as HTMLElement | null)?.dataset.id || '', s: ae.selectionStart, e: ae.selectionEnd } : null;
    if (this.forbidden) {
      body.innerHTML = emptyState('Guest Recovery is limited to office roles', 'Guest follow-ups are handled by the office team — ask a manager if a guest needs smoothing over.', '🔒');
      return;
    }
    if (!this.tickets) {
      body.innerHTML = `
        ${this.unverified
          ? emptyState("Couldn't confirm your access just now", "The server couldn't verify your role — usually a brief connection blip. Try again in a moment.", '⚠️')
          : emptyState('Guest Recovery unavailable', 'GET /api/assetops/issues/guest-recovery failed — check the server log.', '⚠️')}
        <div style="text-align:center; margin-top:10px;"><button type="button" class="btn btn-secondary" id="gr-retry" style="min-height:36px;">↻ Retry</button></div>`;
      document.getElementById('gr-retry')?.addEventListener('click', () => void this.render());
      return;
    }
    const all = this.tickets;
    const counts = this.counts || stateCounts(all);
    const rows = this.fStatus === 'all' ? all : filterRows(all, this.fStatus);
    const want = this.fStatus === 'all' ? counts.all : counts[this.fStatus];
    // R114 fix (review recovery#6): "loading" also while this tab waits behind
    // another tab's fetch (ensureTab chains it) — never its empty-state copy
    const loadingHere = this.fStatus !== 'open' && !!this.loadingTab && !this.loaded.has(this.fStatus) && rows.length < want;

    const tab = (v: Filter) =>
      `<button type="button" class="seg-tab ${this.fStatus === v ? 'active' : ''}" data-gr-f="${v}" role="tab" aria-selected="${this.fStatus === v}">${this.esc(TAB_LABEL[v])} <span class="gr-n">${v === 'all' ? counts.all : counts[v]}</span></button>`;
    const listHtml = rows.length
      ? (this.fStatus === 'blocked_owner' ? this.groupsHtml(rows) : rows.map(r => this.rowHtml(r)).join(''))
      : `<div class="gr-empty">${loadingHere ? 'Loading…' : EMPTY_COPY[this.fStatus]}</div>`;

    body.innerHTML = `
      <style>
        .gr-intro { margin:0; }
        .gr-intro .dash-head { margin-bottom:16px; }
        .gr-banner { font-size:12px; color:var(--success-text); background:var(--success-soft); border:1px solid var(--success-border); border-radius:var(--radius-sm); padding:8px 12px; margin-bottom:12px; }
        .gr-warn, .gr-banner.bad { font-size:12px; color:var(--warning-text); background:var(--warning-soft); border:1px solid var(--warning); border-radius:var(--radius-sm); padding:8px 12px; margin-bottom:12px; line-height:1.5; }
        /* KPI band at the Calendar's density */
        .kpi-grid.gr-kpis { gap:12px; margin-bottom:16px; }
        .gr-kpis .kpi-card { padding:14px 16px; min-width:0; }
        .gr-kpis .kpi-card-value { font-size:24px; margin-top:10px; }
        .gr-kpis .kpi-card-ic { width:34px; height:34px; border-radius:10px; }
        .gr-kpis .kpi-card-cap { margin-top:4px; }
        /* Worklist bar */
        .gr-bar { display:flex; align-items:center; gap:12px; margin-bottom:12px; flex-wrap:wrap; }
        .gr-bar .seg-tabs { max-width:100%; overflow-x:auto; -webkit-overflow-scrolling:touch; scrollbar-width:none; }
        .gr-bar .sub { font-size:12px; color:var(--text-muted); }
        .gr-n { font-variant-numeric:tabular-nums; opacity:.75; font-weight:500; }
        .gr-more { font-size:11.5px; color:var(--text-muted); margin:-4px 0 10px; }
        /* The one data card: rows expand in place */
        .gr-card { background:var(--card); border:1px solid var(--border); border-radius:var(--radius); box-shadow:var(--shadow-card); overflow:hidden; }
        .gr-row { border-bottom:1px solid var(--border-soft); }
        .gr-row:last-child { border-bottom:none; }
        .gr-line { display:flex; align-items:center; gap:12px; padding:12px 18px; cursor:pointer; transition:background .12s ease; }
        .gr-line:hover, .gr-row.expanded > .gr-line { background:var(--bg-subtle); }
        .gr-main { flex:1; min-width:0; }
        .gr-guest { display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
        .gr-name { font-size:13.5px; font-weight:600; color:var(--text); }
        .gr-dates { font-size:11px; color:var(--text-muted); background:var(--bg-subtle); border:1px solid var(--border); border-radius:var(--radius-pill); padding:1px 8px; font-variant-numeric:tabular-nums; }
        .gr-sum { font-size:12px; color:var(--text-muted); margin-top:3px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .gr-right { display:flex; align-items:center; gap:12px; flex:none; }
        .gr-listing { font-size:12px; color:var(--text-muted); max-width:220px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .gr-when { font-size:11.5px; color:var(--text-faint); white-space:nowrap; font-variant-numeric:tabular-nums; }
        .gr-caret { color:var(--text-faint); font-size:12px; width:12px; text-align:center; }
        .gr-detail { border-top:1px solid var(--border-soft); background:var(--bg-subtle); padding:14px 18px 16px; display:flex; flex-direction:column; gap:10px; }
        .gr-excerpt { font-size:12.5px; color:var(--text); background:var(--card); border:1px solid var(--border); border-radius:var(--radius-sm); padding:10px 12px; white-space:pre-wrap; line-height:1.5; }
        .gr-sec { font-size:10.5px; font-weight:600; text-transform:uppercase; letter-spacing:0.06em; color:var(--text-muted); margin-bottom:-4px; }
        /* Activity timeline + composer: shared/components/RecoveryComposer (R115 guest) */
${RECOVERY_COMPOSER_CSS}
        .gr-empty { padding:28px; text-align:center; color:var(--text-muted); font-size:12.5px; }
        /* Blocked-by-owner groups */
        .gr-group + .gr-group { border-top:6px solid var(--bg-subtle); }
        .gr-group-h { display:flex; align-items:center; gap:12px; padding:12px 18px; border-bottom:1px solid var(--border-soft); }
        .gr-group-tt { flex:1; min-width:0; }
        .gr-group-t { font-size:13.5px; font-weight:700; color:var(--text); }
        .gr-group-s { font-size:11.5px; color:var(--text-muted); margin-top:2px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        @media (max-width: 720px) {
          .kpi-grid.gr-kpis { gap:10px; grid-template-columns:repeat(2, minmax(0, 1fr)); }
          .gr-kpis .kpi-card { padding:12px; }
          .gr-line { flex-wrap:wrap; padding:12px 14px; }
          .gr-right { width:100%; justify-content:flex-start; gap:8px; flex-wrap:wrap; }
          .gr-sum { white-space:normal; }
          .gr-listing { max-width:none; }
          .gr-detail { padding:12px 14px 14px; }
          .gr-btn, .gr-bar .seg-tab { min-height:36px; }
          .gr-group-h { flex-wrap:wrap; padding:12px 14px; }
          .gr-group-h .gr-btn { width:100%; justify-content:center; }
        }
      </style>
      <div class="page-intro gr-intro">
        <div class="dash-head">
          <h1>Guest Recovery</h1>
          <p>Guests whose messages read negative (auto-detected hourly), plus anything sent here from the Issues board. Reach out, log each thing you did, then close it with an outcome — Smoothed, Partly resolved, Couldn't resolve or Blocked by owner.</p>
        </div>
      </div>
      ${this.msgSync && this.msgSync.ai_ok === false && this.msgSync.ai_error ? `<div class="gr-warn">⚠ ${this.msgSync.ai_error.kind === 'no_key' ? 'Guest-message AI isn\'t configured (ANTHROPIC_API_KEY missing)' : `Guest-message AI is unavailable (${this.esc(this.msgSync.ai_error.label || 'error')})`}, so new unhappy guests aren't being detected right now. Existing entries are kept.</div>` : ''}
      ${this.note ? `<div class="gr-banner ${this.noteBad ? 'bad' : ''}" role="status">${this.esc(this.note)}</div>` : ''}
      ${all.length || counts.all ? `
        ${this.kpisHtml(all)}
        <div class="gr-bar">
          <div class="seg-tabs" role="tablist">${TABS.map(tab).join('')}</div>
          <span class="sub">${this.fStatus === 'blocked_owner' ? 'Grouped by owner (else by property) · copy a group for the owner call' : 'Newest message first · tap a row to see what\'s been done and log the next step'}</span>
        </div>
        ${!loadingHere && rows.length < want && this.loaded.has(this.fStatus) ? `<div class="gr-more">Showing the newest ${rows.length} of ${want}.</div>` : ''}
        ${loadingHere && rows.length ? `<div class="gr-more">Loading older cases…</div>` : ''}
        <div class="gr-card">${listHtml}</div>`
        : emptyState('No unhappy guests detected 🎉', 'Nothing reads negative right now — the hourly scan keeps watching.', '🤝')}`;
    this.bind();
    // phone width: the tab strip scrolls sideways — keep the active tab in
    // view (horizontal only; never moves the page vertically)
    const strip = body.querySelector<HTMLElement>('.gr-bar .seg-tabs');
    const act = strip && strip.querySelector<HTMLElement>('.seg-tab.active');
    if (strip && act && strip.scrollWidth > strip.clientWidth) {
      strip.scrollLeft += (act.getBoundingClientRect().left - strip.getBoundingClientRect().left) - (strip.clientWidth - act.offsetWidth) / 2;
    }
    if (keep && keep.id) {
      const el = body.querySelector<HTMLTextAreaElement>(`.gr-row[data-id="${CSS.escape(keep.id)}"] [data-role="gr-note"]`);
      if (el) { el.focus(); try { el.setSelectionRange(keep.s ?? el.value.length, keep.e ?? el.value.length); } catch { /* detached */ } }
    }
  }

  // A closed tab's rows may sit past the default list's cap / 30-day case
  // window — fetch that tab once (?state= bypasses both) and merge by id.
  private async ensureTab(f: Filter): Promise<void> {
    if (f === 'open' || !this.tickets || !this.counts || this.loaded.has(f) || this.loadingTab) return;
    const want = f === 'all' ? this.counts.all : this.counts[f];
    if ((f === 'all' ? this.tickets.length : filterRows(this.tickets, f).length) >= want) { this.loaded.add(f); return; }
    this.loadingTab = f;
    this.draw();
    const j = await this.fetchList({ state: f });
    this.loadingTab = '';
    if (j && j.forbidden) this.forbidden = true;
    if (j && !j.forbidden && Array.isArray(j.tickets) && this.tickets) {
      const byId = new Map(this.tickets.map((t: any) => [String(t.id), t]));
      for (const r of j.tickets) if (!byId.has(String(r.id))) byId.set(String(r.id), r);   // a row already here keeps its local (possibly just-patched) copy
      this.tickets = [...byId.values()].sort((a: any, b: any) => parseTs(b.last_message_at) - parseTs(a.last_message_at));
      if (j.counts) this.counts = j.counts;
      if (j.kpis) this.kpis = j.kpis;
      this.loaded.add(f);
    } else {
      this.note = "Couldn't load the older cases for this tab — showing the newest ones.";
      this.noteBad = true;
    }
    this.draw();
    // R114 fix (review recovery#6): a tab clicked while this fetch was in
    // flight was dropped by the loadingTab gate above — load it now. Can't
    // loop: it runs only after a switch, and ensureTab's own open / loaded /
    // count-met gates stop it.
    const cur = this.fStatus;
    if (cur !== f && cur !== 'open' && !this.loaded.has(cur) && !this.forbidden) void this.ensureTab(cur);
  }

  private bind(): void {
    bindKebabs(this.container);
    this.container.querySelectorAll<HTMLElement>('[data-gr-f]').forEach(el => el.addEventListener('click', () => {
      this.fStatus = (el.dataset.grF as Filter) || 'open';
      this.draw();
      void this.ensureTab(this.fStatus);
    }));
    this.container.querySelectorAll<HTMLElement>('[data-gr-copy]').forEach(el => el.addEventListener('click', () => void this.copyGroup(el.dataset.grCopy || '')));
    this.container.querySelectorAll<HTMLElement>('.gr-row[data-id]').forEach(row => {
      const id = row.dataset.id!;
      const t = (this.tickets || []).find((x: any) => String(x.id) === id);
      if (!t) return;
      row.querySelector('[data-tog]')?.addEventListener('click', () => {
        if (this.open.has(id)) this.open.delete(id); else this.open.add(id);
        this.draw();
      });
      // the composer draft lives on the instance + per viewer in localStorage —
      // never lost to a redraw / reload, never sent until a step is logged
      const noteEl = row.querySelector<HTMLTextAreaElement>('[data-role="gr-note"]');
      noteEl?.addEventListener('input', () => {
        const v = noteEl.value;
        this.setDraft(id, v);
        row.querySelector('.gr-draft')?.toggleAttribute('hidden', !v.trim());
        if (this.errs[id] && v.trim()) { delete this.errs[id]; row.querySelector('.gr-err')?.remove(); }
      });
      row.querySelectorAll<HTMLElement>('[data-act]').forEach(btn => btn.addEventListener('click', () => {
        const act = btn.dataset.act as 'log' | 'close' | 'reopen';
        void this.act(t, act, (btn.dataset.outcome || undefined) as RecoveryOutcome | undefined);
      }));
    });
  }

  private focusComposer(id: string): void {
    const el = this.container.querySelector<HTMLTextAreaElement>(`.gr-row[data-id="${CSS.escape(id)}"] [data-role="gr-note"]`);
    if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length); }
  }

  // One recovery step: busy-gated POST, the row is patched from the server's
  // answer (no optimistic flip), the draft clears only on success, the
  // counts / KPIs move by exactly this row's change.
  private async act(t: any, op: 'log' | 'close' | 'reopen', outcome?: RecoveryOutcome): Promise<void> {
    if (this.busy) return;
    closeKebab();
    const id = String(t.id);
    const text = this.draftOf(id).trim();
    const guard = composerGuard(op, outcome, text);   // R115 guest: the shared early note rule
    if (guard) { this.errs[id] = guard; this.draw(); this.focusComposer(id); return; }
    delete this.errs[id];
    this.busy = true;
    this.note = 'Saving…'; this.noteBad = false;
    this.draw();
    const guest = String(t.guest_name || 'guest').slice(0, 40);
    try {
      const r = await fetch(`${API}/issues/guest-recovery/${encodeURIComponent(id)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ op, ...(op === 'close' ? { outcome } : {}), text, by: this.who() || undefined, by_name: this.whoName() || undefined }),
      });
      const j = await r.json().catch(() => ({} as any));
      if (!r.ok) {
        if (j && j.code === 'note_required') this.errs[id] = String(j.error || 'A note is required for this outcome.');
        throw new Error((j && j.error) || `HTTP ${r.status}`);
      }
      const before = { ...t };
      if (j.recovery) Object.assign(t, j.recovery);
      const agg = bumpAggregates({ counts: this.counts, kpis: this.kpis }, before, t, Date.now(), this.todayPT());
      this.counts = agg.counts; this.kpis = agg.kpis;
      this.setDraft(id, '');
      const label = STATE_LABEL[stateOf(t)];
      this.note = j.unchanged ? `Already ${label} — nothing changed.`
        : op === 'log' ? `Logged — ${guest}${isClosed(t) ? ` (still ${label})` : ''}.`
        : op === 'reopen' ? `Reopened — ${guest}.`
        : `Closed as ${label} — ${guest}.`;
      this.noteBad = false;
    } catch (e: any) {
      this.note = `Didn't save — ${e.message}. Your note is still in the box.`;
      this.noteBad = true;
    }
    this.busy = false;
    this.draw();
  }

  // "Copy for owner call": date · property · ask · what we did / the owner's
  // answer — no guest names, phones or emails (ownerCallText scrubs them).
  private async copyGroup(key: string): Promise<void> {
    const g = this.groups.find(x => x.key === key);
    if (!g) return;
    const text = ownerCallText(g);
    let ok = false;
    try { await navigator.clipboard.writeText(text); ok = true; } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select();
        ok = document.execCommand('copy');
        ta.remove();
      } catch { ok = false; }
    }
    this.note = ok ? `Copied ${g.rows.length} request${g.rows.length === 1 ? '' : 's'} for ${g.label} — no guest names or phone numbers included.` : "Couldn't reach the clipboard — try again, or open each case.";
    this.noteBad = !ok;
    this.draw();
  }
}
